import type { CatalogueObject, ObjectValue } from "../types/catalog";
import { formatUtcDate } from "../helpers/utils";

const formatNumber = (value: number | undefined, units?: string) =>
  value === undefined
    ? ""
    : `${value.toLocaleString()}${units ? ` ${units}` : ""}`;

const formatWithUnits = (value: string | number | boolean, units?: string) =>
  `${String(value)}${units ? ` ${units}` : ""}`;

export const getCatalogSearchValues = (object: CatalogueObject): string[] =>
  [
    getPropertyObjectValue(object.name),
    getPropertyObjectValue(object.noradId),
    getPropertyObjectValue(object.cosparId),
    getPropertyObjectValue(object.operator),
    getPropertyObjectValue(object.regime),
    getPropertyObjectValue(object.status),
    getPropertyObjectValue(object.typeOfObject),
    getPropertyObjectValue(object.launchDate),
    getPropertyObjectValue(object.country),
    getPropertyObjectValue(object.launchSite),
    getPropertyObjectValue(object.source),
    getPropertyObjectValue(object.latestUpdate),
    getPropertyObjectValue(object.createdAt),
  ].filter(hasValue);

export const getDisplayObjectValue = <T>(
  property?: ObjectValue<T> | null,
  defaultUnits?: string,
): string => getPropertyObjectValue(property, defaultUnits);

export const getCatalogDetailRows = (object: CatalogueObject) =>
  [
    ["Satellite name", getPropertyObjectValue(object.name)],
    ["NORAD", getPropertyObjectValue(object.noradId)],
    ["COSPAR", getPropertyObjectValue(object.cosparId)],
    ["Source", getPropertyObjectValue(object.source)],
    ["Latest update (UTC)", formatUtcDate(object.latestUpdate?.value)],
    ["Creation date (UTC)", formatUtcDate(object.createdAt?.value)],
    ["Organization", getPropertyObjectValue(object.operator)],
    ["Type of object", getPropertyObjectValue(object.typeOfObject)],
    ["Status", getPropertyObjectValue(object.status)],
    ["Country", getPropertyObjectValue(object.country)],
    ["Launch site", getPropertyObjectValue(object.launchSite)],
    ["Launch date (UTC)", formatUtcDate(object.launchDate?.value)],
    ["Decay date (UTC)", formatUtcDate(object.decayDate?.value)],
    ["RCS", getPropertyObjectValue(object.rcs, "m^2")],
    ["Mass", getPropertyObjectValue(object.mass, "kg")],
    ["Hard body radius", getPropertyObjectValue(object.hbr, "m")],
    ["Length", getPropertyObjectValue(object.length, "m")],
    ["Width", getPropertyObjectValue(object.width, "m")],
    ["Span", getPropertyObjectValue(object.span, "m")],
    ["Average area", getPropertyObjectValue(object.avgArea, "m^2")],
    ["Shape", getPropertyObjectValue(object.shape)],
    ["Orbital period", getPropertyObjectValue(object.period)],
    ["Perigee height", getPropertyObjectValue(object.perigee)],
    ["Apogee height", getPropertyObjectValue(object.apogee)],
    ["Orbital regime", getPropertyObjectValue(object.regime)],
    ["Inclination", getPropertyObjectValue(object.inclination, "deg")],
    ["RAAN", getPropertyObjectValue(object.raan, "deg")],
    ["Argument of perigee", getPropertyObjectValue(object.aop, "deg")],
  ].filter(([, value]) => hasValue(value));

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && String(value).trim() !== "";

// ALPHA-5 support. I and O are omitted to avoid confusion with 1 and 0.
const ALPHA5_ALPHABET = "0123456789ABCDEFGHJKLMNPQRSTUVWXYZ";

// Reverse lookup for alpha5-norad conversion.
const CHAR_TO_INDEX: Record<string, number> = {};
for (let i = 0; i < ALPHA5_ALPHABET.length; i++) {
  CHAR_TO_INDEX[ALPHA5_ALPHABET[i]] = i;
}

const NORMALIZED_NORAD_SEPARATOR_PATTERN = /[,\s._\u202F]/g;
const GROUPED_NORAD_PATTERN = /^\d{1,3}(?:[,\s._\u202F]\d{3})+$/;

const normalizeNoradLikeInput = (
  value: string | number,
): { kind: "empty" | "numeric" | "alpha5" | "invalid"; value: string } => {
  if (typeof value === "number") {
    if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) {
      return { kind: "invalid", value: String(value) };
    }

    return { kind: "numeric", value: String(value) };
  }

  const raw = String(value).trim().toUpperCase();

  if (!raw) {
    return { kind: "empty", value: "" };
  }

  if (
    raw.length === 5 &&
    Object.prototype.hasOwnProperty.call(CHAR_TO_INDEX, raw[0]) &&
    CHAR_TO_INDEX[raw[0]] >= 10 &&
    /^\d{4}$/.test(raw.slice(1))
  ) {
    return { kind: "alpha5", value: raw };
  }

  const compact = raw.replace(NORMALIZED_NORAD_SEPARATOR_PATTERN, "");

  if (/^\d+$/.test(compact) && GROUPED_NORAD_PATTERN.test(raw)) {
    return { kind: "numeric", value: compact };
  }

  return { kind: "invalid", value: raw };
};

// ============================================================================

export function norad2alpha5(norad: string | number): string {
  /**
   * Convert a NORAD catalogue number to Alpha-5 when needed.
   */

  const normalized = normalizeNoradLikeInput(norad);

  if (normalized.kind === "empty") {
    return "";
  }

  if (normalized.kind === "numeric") {
    const i_norad_int = parseInt(normalized.value, 10);

    // Norad out of alpha-5 range
    if (i_norad_int > 339999) {
      throw new Error(
        `NORAD number ${i_norad_int} out of supported range (0-339 999).`,
      );
    }

    let alpha5: string;

    // Norad in 5-digit range (no Alpha-5 conversion is needed)
    if (i_norad_int < 100000) {
      alpha5 = i_norad_int.toString().padStart(5, "0");
    } else {
      const i_base = Math.floor(i_norad_int / 10000);
      const s_code = ALPHA5_ALPHABET[i_base];
      const i_num = i_norad_int - i_base * 10000;
      alpha5 = `${s_code}${i_num.toString().padStart(4, "0")}`;
    }

    return alpha5;
  }

  if (normalized.kind === "alpha5") {
    return normalized.value;
  }

  throw new Error(
    `String '${normalized.value}' is not a valid NORAD number or alpha-5`,
  );
}

export function alpha52norad(sAlpha5: string | number): number {
  /**
   * Convert an Alpha-5 string to decimal NORAD when needed.
   *
   * If the input already looks like a valid NORAD identifier in decimal form,
   * it is returned unchanged as a number.
   *
   * If the input is not a valid NORAD identifier, it fails.
   */

  const s = String(sAlpha5).trim().toUpperCase();

  if (!s) {
    throw new Error("Empty string cannot be converted to a NORAD number");
  }

  if (/^\d+$/.test(s)) {
    const norad = Number.parseInt(s, 10);

    // If NORAD has more than 9 digits, it is invalid
    if (norad > 999_999_999 || norad < 0) {
      throw new Error(`String '${s}' is not a valid NORAD number`);
    }

    return norad;
  }

  if (
    s.length === 5 &&
    Object.prototype.hasOwnProperty.call(CHAR_TO_INDEX, s[0])
  ) {
    const iBase = CHAR_TO_INDEX[s[0]];
    const tail = s.slice(1, 5);

    if (!/^\d{4}$/.test(tail)) {
      throw new Error(`Alpha-5 tail '${tail}' is not a four-digit integer`);
    }

    if (iBase < 10 || iBase > 33) {
      throw new Error(`Alpha-5 code '${s[0]}' is out of supported range`);
    }

    const iNum = Number.parseInt(tail, 10);
    const norad = iBase * 10_000 + iNum;

    return norad;
  }

  throw new Error(`String '${s}' is not a valid NORAD number or alpha-5`);
}

export const getPropertyObjectValue = <T>(
  property?: ObjectValue<T> | null,
  defaultUnits?: string,
): string => {
  if (property == null || property.value == null) {
    return "";
  }

  const { value, units } = property;

  if (typeof value === "number") {
    return formatNumber(value, units ?? defaultUnits);
  }

  if (typeof value === "string") {
    return formatWithUnits(value, defaultUnits ?? units);
  }

  if (typeof value === "boolean") {
    return formatWithUnits(value ? "Yes" : "No", defaultUnits ?? units);
  }

  return String(value);
};
